import React, { useRef, useCallback } from 'react';
import { Rnd } from 'react-rnd';
import './FloatingChatWindow.css';

const FloatingChatWindow = ({
  children,
  position,
  size,
  onPositionChange,
  onSizeChange,
  onDock,
  isDarkMode,
  minWidth = 300,
  minHeight = 400,
  maxWidth = 800,
  maxHeight = null
}) => {
  const windowRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const maxHeightValue = maxHeight || window.innerHeight * 0.9;
  
  // Use 'window' bounds - react-rnd will automatically keep window within viewport
  // This is more reliable than custom bounds calculation
  const bounds = 'window';
  
  const handleDragStop = useCallback((e, d) => {
    // react-rnd already handles bounds, just save the position
    // d.x and d.y are already clamped by react-rnd when bounds='window'
    // Ensure we never save invalid positions (0,0 or negative)
    const newX = Math.max(0, d.x);
    const newY = Math.max(0, d.y);
    onPositionChange({ x: newX, y: newY });
  }, [onPositionChange]);
  
  // Debounced resize handler for real-time updates during resize
  const handleResize = useCallback((e, direction, ref, delta, position) => {
    if (resizeTimeoutRef.current) {
      cancelAnimationFrame(resizeTimeoutRef.current);
    }
    
    resizeTimeoutRef.current = requestAnimationFrame(() => {
      const newSize = {
        width: parseInt(ref.style.width, 10),
        height: parseInt(ref.style.height, 10)
      };
      const newPosition = {
        x: Math.max(0, Math.min(position.x, window.innerWidth - newSize.width)),
        y: Math.max(0, Math.min(position.y, window.innerHeight - newSize.height))
      };
      
      onSizeChange(newSize);
      onPositionChange(newPosition);
    });
  }, [onSizeChange, onPositionChange]);
  
  const handleResizeStop = useCallback((e, direction, ref, delta, position) => {
    // Cancel any pending resize updates
    if (resizeTimeoutRef.current) {
      cancelAnimationFrame(resizeTimeoutRef.current);
    }
    
    const newSize = {
      width: parseInt(ref.style.width, 10),
      height: parseInt(ref.style.height, 10)
    };
    const newPosition = {
      x: Math.max(0, Math.min(position.x, window.innerWidth - newSize.width)),
      y: Math.max(0, Math.min(position.y, window.innerHeight - newSize.height))
    };
    
    onSizeChange(newSize);
    onPositionChange(newPosition);
  }, [onSizeChange, onPositionChange]);
  
  // Ensure position is always valid (not undefined or null)
  // Calculate default position on right side if position is invalid
  const defaultX = window.innerWidth - (size?.width || 400) - 20;
  const defaultY = 100;
  
  const validPosition = position && typeof position.x === 'number' && typeof position.y === 'number' && 
                        !isNaN(position.x) && !isNaN(position.y) && position.x >= 0 && position.y >= 0
    ? position
    : { x: defaultX, y: defaultY };
  
  // Ensure size is always valid
  const validSize = size && size.width && size.height
    ? size
    : { width: 400, height: 600 };

  return (
    <Rnd
      ref={windowRef}
      size={validSize}
      position={validPosition}
      minWidth={minWidth}
      minHeight={minHeight}
      maxWidth={maxWidth}
      maxHeight={maxHeightValue}
      bounds={bounds}
      onDragStop={handleDragStop}
      onResize={handleResize}
      onResizeStop={handleResizeStop}
      dragHandleClassName="floating-window-header"
      enableDragging={true}
      enableResizing={{
        top: true,
        right: true,
        bottom: true,
        left: true,
        topRight: true,
        bottomRight: true,
        bottomLeft: true,
        topLeft: true
      }}
      className={`floating-chat-window ${isDarkMode ? 'dark-mode' : ''}`}
    >
      <div className="floating-window-header">
        <div className="floating-window-title">
          Chat
        </div>
        <div className="floating-window-controls">
          <button
            className="floating-window-button dock-button"
            onClick={onDock}
            onMouseDown={(e) => e.stopPropagation()} // Prevent drag when clicking button
            aria-label="Dock chat panel"
            title="Dock chat panel"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 2L2 8h4v6h4V8h4L8 2z"/>
            </svg>
          </button>
        </div>
      </div>
      <div className="floating-window-content">
        {children}
      </div>
    </Rnd>
  );
};

export default FloatingChatWindow;

